import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Plus, Sparkles, Search } from 'lucide-react';
import clsx from 'clsx';
import { storiesApi, StoryGroup } from '@/api/stories';
import { useAuthStore } from '@/store/authStore';
import Avatar from '@/components/ui/Avatar';
import StoryViewer from './StoryViewer';
import StoryCreateModal from './StoryCreateModal';
import ForYouFeed from './ForYouFeed';
import HashtagView from './HashtagView';
import DiscoverPage from './DiscoverPage';
import SoundPage from './SoundPage';

export default function StoryTray() {
  const { user, isAuthenticated } = useAuthStore();
  const [viewGroup, setViewGroup] = useState<StoryGroup | null>(null);
  const [creating, setCreating] = useState(false);
  const [showForYou, setShowForYou] = useState(false);
  const [hashtagView, setHashtagView] = useState<string | null | false>(false);
  const [showDiscover, setShowDiscover] = useState(false);
  const [soundPage, setSoundPage] = useState<string | null>(null);

  // v5.7.11 — circuit breaker для unrecovered 401 после axios refresh/retry.
  // Если /api/stories/feed отдал 401 после интерсептора — гасим polling до auth-event.
  // Иначе refetchInterval 60s × broken auth = console-spam (17 401 за 17 минут у admin'а).
  const [feedAuthBlocked, setFeedAuthBlocked] = useState(false);

  // Reset blocker при смене user (logout→login) или потере auth.
  useEffect(() => {
    setFeedAuthBlocked(false);
  }, [isAuthenticated, user?.id]);

  const feedQuery = useQuery({
    queryKey: ['stories', 'feed'],
    queryFn: storiesApi.getFeed,
    enabled: isAuthenticated && !feedAuthBlocked,
    refetchInterval: feedAuthBlocked ? false : 60_000,
    refetchOnWindowFocus: isAuthenticated && !feedAuthBlocked,
    retry: false,
  });

  useEffect(() => {
    if (axios.isAxiosError(feedQuery.error) && feedQuery.error.response?.status === 401) {
      setFeedAuthBlocked(true);
    }
  }, [feedQuery.error]);

  const feed = feedQuery.data;
  const refetch = feedQuery.refetch;

  if (!feed || feed.length === 0) {
    return (
      <div className="flex items-center gap-3 px-3 py-2 border-b border-surface-800 overflow-x-auto scrollbar-none">
        <button
          onClick={() => setCreating(true)}
          className="flex flex-col items-center gap-1 flex-shrink-0"
        >
          <div className="w-12 h-12 rounded-full border-2 border-dashed border-surface-600 flex items-center justify-center text-surface-400 hover:border-brand-500 hover:text-brand-400 transition-colors">
            <Plus size={20} />
          </div>
          <span className="text-[10px] text-surface-500">Добавить</span>
        </button>
        <button
          onClick={() => setShowForYou(true)}
          className="flex flex-col items-center gap-1 flex-shrink-0"
        >
          <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-tr from-pink-500 to-brand-500 text-white">
            <Sparkles size={20} />
          </div>
          <span className="text-[10px] text-surface-400">Тебе</span>
        </button>
        <button
          onClick={() => setShowDiscover(true)}
          className="flex flex-col items-center gap-1 flex-shrink-0"
        >
          <div className="w-12 h-12 rounded-full flex items-center justify-center bg-surface-800 text-surface-200">
            <Search size={20} />
          </div>
          <span className="text-[10px] text-surface-400">Найти</span>
        </button>

        {creating && (
          <StoryCreateModal
            onClose={() => setCreating(false)}
            onCreated={() => { setCreating(false); refetch(); }}
          />
        )}
        {showForYou && (
          <div className="fixed inset-0 z-50 bg-black">
            <button
              onClick={() => setShowForYou(false)}
              className="absolute top-4 left-4 z-10 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center"
              aria-label="Закрыть"
            >
              ✕
            </button>
            <ForYouFeed onOpenTag={(tag) => { setShowForYou(false); setHashtagView(tag); }} />
          </div>
        )}
        {hashtagView !== false && (
          <div className="fixed inset-0 z-50">
            <HashtagView
              tag={hashtagView}
              onBack={() => setHashtagView(false)}
              onOpenTag={(tag) => setHashtagView(tag)}
            />
          </div>
        )}
        {showDiscover && (
          <DiscoverPage
            onClose={() => setShowDiscover(false)}
            onOpenTag={(tag) => { setShowDiscover(false); setHashtagView(tag); }}
            onOpenSound={(id) => { setShowDiscover(false); setSoundPage(id); }}
          />
        )}
        {soundPage && (
          <SoundPage trackId={soundPage} onClose={() => setSoundPage(null)} />
        )}
      </div>
    );
  }

  const ownGroup = feed.find((g) => g.author.id === user?.id);
  const otherGroups = feed.filter((g) => g.author.id !== user?.id);

  return (
    <>
      <div className="flex items-center gap-3 px-3 py-2 border-b border-surface-800 overflow-x-auto scrollbar-none">
        {/* Add new story button */}
        <button
          onClick={() => setCreating(true)}
          className="flex flex-col items-center gap-1 flex-shrink-0"
        >
          <div className="w-12 h-12 rounded-full border-2 border-dashed border-surface-600 flex items-center justify-center text-surface-400 hover:border-brand-500 hover:text-brand-400 transition-colors">
            <Plus size={20} />
          </div>
          <span className="text-[10px] text-surface-500">Добавить</span>
        </button>

        {/* Phase 11 — For You */}
        <button
          onClick={() => setShowForYou(true)}
          className="flex flex-col items-center gap-1 flex-shrink-0"
        >
          <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-tr from-pink-500 to-brand-500 text-white">
            <Sparkles size={20} />
          </div>
          <span className="text-[10px] text-surface-400">Тебе</span>
        </button>
        <button
          onClick={() => setShowDiscover(true)}
          className="flex flex-col items-center gap-1 flex-shrink-0"
        >
          <div className="w-12 h-12 rounded-full flex items-center justify-center bg-surface-800 text-surface-200">
            <Search size={20} />
          </div>
          <span className="text-[10px] text-surface-400">Найти</span>
        </button>

        {/* Own stories (if any) */}
        {ownGroup && (
          <button
            onClick={() => setViewGroup(ownGroup)}
            className="flex flex-col items-center gap-1 flex-shrink-0"
          >
            <div className={clsx(
              'w-12 h-12 rounded-full p-[2px]',
              ownGroup.hasUnviewed
                ? 'bg-gradient-to-tr from-brand-500 to-purple-500'
                : 'bg-surface-600',
            )}>
              <Avatar
                username={ownGroup.author.username}
                src={ownGroup.author.avatarUrl}
                size="lg"
                className="border-2 border-surface-950"
              />
            </div>
            <span className="text-[10px] text-surface-400 truncate max-w-[56px]">Моя</span>
          </button>
        )}

        {/* Other users' stories */}
        {otherGroups.map((group) => (
          <button
            key={group.author.id}
            onClick={() => setViewGroup(group)}
            className="flex flex-col items-center gap-1 flex-shrink-0"
          >
            <div className={clsx(
              'w-12 h-12 rounded-full p-[2px]',
              group.hasUnviewed
                ? 'bg-gradient-to-tr from-brand-500 to-purple-500'
                : 'bg-surface-600',
            )}>
              <Avatar
                username={group.author.username}
                src={group.author.avatarUrl}
                size="lg"
                className="border-2 border-surface-950"
              />
            </div>
            <span className="text-[10px] text-surface-400 truncate max-w-[56px]">
              {group.author.displayName.split(' ')[0]}
            </span>
          </button>
        ))}
      </div>

      {viewGroup && (
        <StoryViewer
          group={viewGroup}
          onClose={() => { setViewGroup(null); refetch(); }}
        />
      )}

      {creating && (
        <StoryCreateModal
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); refetch(); }}
        />
      )}

      {showForYou && (
        <div className="fixed inset-0 z-50 bg-black">
          <button
            onClick={() => setShowForYou(false)}
            className="absolute top-4 left-4 z-10 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center"
            aria-label="Закрыть"
          >
            ✕
          </button>
          <ForYouFeed onOpenTag={(tag) => { setShowForYou(false); setHashtagView(tag); }} />
        </div>
      )}

      {hashtagView !== false && (
        <div className="fixed inset-0 z-50">
          <HashtagView
            tag={hashtagView}
            onBack={() => setHashtagView(false)}
            onOpenTag={(tag) => setHashtagView(tag)}
          />
        </div>
      )}
    </>
  );
}
